/**
 * 认证状态管理 (Zustand)
 *
 * 管理用户登录状态，后台会话由 HttpOnly Cookie 承载
 */

import { create } from 'zustand';
import { authApi } from '../services/api';
import { getCookie, deleteCookie } from '../utils/cookie';

export interface AuthUser {
    id: string;
    username: string;
    email: string;
    is_active: boolean;
    created_at: string;
}

interface AuthState {
    user: AuthUser | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    /** 用户登录 */
    login: (username: string, password: string) => Promise<boolean>;

    /** 用户登出 */
    logout: () => Promise<void>;

    /** 检查并恢复登录状态 */
    checkAuth: () => Promise<boolean>;

    /** 清除错误 */
    clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,

    login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });

        // 获取当前的匿名 ID（用于登录后清除）
        const anonymousId = getCookie('anonymous_id');

        try {
            await authApi.login(username, password);

            // 获取用户信息
            const userRes = await authApi.me();
            const user = userRes.data;

            set({
                token: null,
                user,
                isAuthenticated: true,
                isLoading: false,
            });

            // 登录成功后清除匿名 ID
            if (anonymousId) {
                deleteCookie('anonymous_id');
                console.log('[authStore] 已清除匿名 ID Cookie');

                // 触发会话列表刷新（后端已自动合并会话）
                const { useChatStore } = await import('./chatStore');
                const chatStore = useChatStore.getState();
                await chatStore.fetchSessions();
                console.log('[authStore] 已刷新会话列表');
            }

            return true;
        } catch (err: unknown) {
            const message =
                typeof err === 'object' &&
                err !== null &&
                'response' in err &&
                typeof err.response === 'object' &&
                err.response !== null &&
                'data' in err.response &&
                typeof err.response.data === 'object' &&
                err.response.data !== null &&
                'detail' in err.response.data &&
                typeof err.response.data.detail === 'string'
                    ? err.response.data.detail
                    : '登录失败，请检查用户名和密码';
            set({ isLoading: false, error: message });
            return false;
        }
    },

    logout: async () => {
        await authApi.logout();
        set({
            user: null,
            token: null,
            isAuthenticated: false,
        });
    },

    checkAuth: async () => {
        try {
            const res = await authApi.me();
            set({
                user: res.data,
                token: null,
                isAuthenticated: true,
            });
            return true;
        } catch {
            set({ isAuthenticated: false, user: null, token: null });
            return false;
        }
    },

    clearError: () => set({ error: null }),
}));
